/**
 * ListingCard — displays a single listing preview in a grid
 *
 * Client component for interactivity (favorite toggle, hover effects).
 * Glassmorphism card with HandiPrêt branding.
 */

"use client";

import { useState, useCallback } from "react";
import Image from "next/image";
import Link from "next/link";
import { Heart, MapPin } from "lucide-react";
import { useSession } from "next-auth/react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LISTING_TYPES, ITEM_CONDITIONS } from "@/lib/constants";
import { toast } from "sonner";
import type { ListingType, ItemCondition } from "@/generated/prisma/client";

interface ListingCardProps {
    listing: {
        id: string;
        title: string;
        slug: string;
        type: ListingType;
        condition: ItemCondition;
        price: number | null;
        city: string;
        postalCode: string;
        createdAt: string | Date;
        images: { url: string; alt: string | null }[];
        category: { name: string; slug: string } | null;
        user: { name: string | null; image: string | null } | null;
        _count?: { favorites: number };
    };
    /** Whether the listing is already favorited by the current user */
    initialFavorited?: boolean;
}

/** Format price in euros */
function formatPrice(price: number | null, type: ListingType): string {
    if (type === "DONATION") return "Gratuit";
    if (type === "LOAN") return "Prêt";
    if (price == null) return "Prix non défini";
    return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
    }).format(price);
}

/** Map listing type to gradient */
function typeGradient(type: ListingType): string {
    switch (type) {
        case "DONATION": return "from-hp-orange to-hp-orange-dark";
        case "LOAN": return "from-hp-purple to-hp-purple-dark";
        case "SALE": return "from-hp-teal to-hp-teal-light";
        default: return "from-hp-purple to-hp-orange";
    }
}

export function ListingCard({ listing, initialFavorited = false }: ListingCardProps) {
    const { data: session } = useSession();
    const [favorited, setFavorited] = useState(initialFavorited);
    const [favCount, setFavCount] = useState(listing._count?.favorites ?? 0);

    const toggleFavorite = useCallback(
        async (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();

            if (!session?.user) {
                toast.error("Connectez-vous pour ajouter aux favoris");
                return;
            }

            // Optimistic update
            const wasFavorited = favorited;
            setFavorited(!wasFavorited);
            setFavCount((c) => (wasFavorited ? c - 1 : c + 1));

            try {
                const res = await fetch("/api/favorites", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ listingId: listing.id }),
                });

                if (!res.ok) {
                    // Revert
                    setFavorited(wasFavorited);
                    setFavCount((c) => (wasFavorited ? c + 1 : c - 1));
                    toast.error("Erreur lors de la mise à jour des favoris");
                    return;
                }

                const data = await res.json();
                setFavorited(data.favorited);
            } catch {
                // Revert
                setFavorited(wasFavorited);
                setFavCount((c) => (wasFavorited ? c + 1 : c - 1));
                toast.error("Erreur réseau");
            }
        },
        [session, favorited, listing.id],
    );

    const typeConfig = LISTING_TYPES[listing.type];
    const conditionConfig = ITEM_CONDITIONS[listing.condition];
    const thumbnail = listing.images[0];

    return (
        <div className="glass group overflow-hidden rounded-2xl transition-all hover:shadow-hp-hover">
            <Link href={`/annonces/${listing.slug}`} className="block">
                {/* Image */}
                <div className="relative aspect-[4/3] overflow-hidden bg-hp-gray/5">
                    {thumbnail ? (
                        <Image
                            src={thumbnail.url}
                            alt={thumbnail.alt ?? listing.title}
                            fill
                            className="object-cover transition-transform duration-300 group-hover:scale-105"
                            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                        />
                    ) : (
                        <div className="flex h-full items-center justify-center text-hp-gray-light">
                            <span className="text-sm">Pas de photo</span>
                        </div>
                    )}

                    {/* Type badge — gradient */}
                    <Badge
                        className={`absolute left-3 top-3 border-0 bg-gradient-to-r ${typeGradient(listing.type)} text-white shadow-hp`}
                    >
                        {typeConfig.label}
                    </Badge>

                    {/* Favorite button */}
                    <Button
                        variant="ghost"
                        size="icon"
                        className={`absolute right-3 top-3 h-9 w-9 rounded-full backdrop-blur-sm transition-colors ${favorited
                                ? "bg-hp-orange/90 text-white hover:bg-hp-orange"
                                : "bg-white/80 hover:bg-white hover:text-hp-purple"
                            }`}
                        onClick={toggleFavorite}
                        aria-label={favorited ? "Retirer des favoris" : "Ajouter aux favoris"}
                    >
                        <Heart
                            className={`h-4 w-4 ${favorited ? "fill-current" : ""}`}
                            aria-hidden="true"
                        />
                    </Button>
                </div>

                {/* Content */}
                <div className="p-4">
                    <h3 className="line-clamp-2 font-bold leading-tight text-hp-gray group-hover:text-hp-purple transition-colors">
                        {listing.title}
                    </h3>

                    <div className="mt-2 flex items-center gap-2 text-sm text-hp-gray-light">
                        <MapPin className="h-3.5 w-3.5 shrink-0 text-hp-teal" aria-hidden="true" />
                        <span className="truncate">
                            {listing.city} ({listing.postalCode.slice(0, 2)})
                        </span>
                    </div>

                    <div className="mt-3 flex items-center justify-between">
                        <span className="text-lg font-black gradient-text">
                            {formatPrice(listing.price as number | null, listing.type)}
                        </span>
                        {conditionConfig && (
                            <Badge variant="outline" className="text-xs border-hp-gray/20 text-hp-gray-light">
                                {conditionConfig.label}
                            </Badge>
                        )}
                    </div>

                    {listing.category && (
                        <p className="mt-2 text-xs text-hp-gray-light font-medium">
                            {listing.category.name}
                        </p>
                    )}
                </div>
            </Link>
        </div>
    );
}
